import "./style.scss";

import Button, { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { DocumentBasylic, ErrorType } from "shared/types/types";
import React, { FC, useContext, useEffect, useState } from "react";
import {
    generateReportWithShareIdPhoto, generateReportWithShareIDVideo,
    getDocumentHighestAnalysisEver,
} from "features/analysis";

import Alert from "shared/ui/Alert";
import DocumentTypeSelect from "../DocumentTypeSelect";
import DocumentUpload from "../DocumentUpload/DocumentUpload";
import FileDownloadInstructions from "../FileDownloadInstructions";
import ShareIDVerification from "../ShareIDVerification";
import { UserContext } from "features/account";
import BuyCreditsCard from "../BuyCreditsCard";
import {formatShareIDPhotoResultToDocument} from "../../utils/utils";
import { canStartDetection, consumeDetection, DetectionTool, loadDetectionQuota, NO_VERIFICATION_LEFT } from "features/credits";
import SponsorShipModal from "../SponsorShipModal";
import { getShareIdTokenAndApplicantId } from "features/share-id";

type AddDocumentFormProps = {
    onChangeType: (type: string) => void;
    type: string;
    setIsScanning: (isScanning: boolean) => void;
    setIsScanningLoadingText: (isScanningLoadingText: string) => void;
    allowForeignDocuments?: boolean;
    isScanning?: boolean;
    setErrorMessage?: (errorMessage: string) => void;
    setIsScanned?: (isScanned: boolean) => void;
    setDocument?: (document: DocumentBasylic) => void;
    isVerification?: boolean;
    setErrorType?: (errorType: ErrorType) => void;
    errorType?: ErrorType;
    setVerifyReportURL?: (url: string) => void;
    isDarkWeb?: boolean;
    isByVideo?: boolean;
    setApplicant_id: (uid: string) => void;
    applicant_id?: string;
    setAccessToken: (uid: string) => void;
    accessToken?: string;
    data?: any;
    setData?: (data: any) => void;
    backToChoice?: () => void; // Fonction pour gérer un retour à un choix précédent
};

const AddDocumentForm: FC<AddDocumentFormProps> = ({
    onChangeType,
    setIsScanning,
   setIsScanningLoadingText,
    type,
    allowForeignDocuments,
    setErrorMessage,
    setIsScanned,
    setDocument,
    isScanning,
    isVerification,
    setErrorType,
    errorType,
    setVerifyReportURL,
    isDarkWeb,
    isByVideo = false,
    applicant_id,
    setApplicant_id,
   accessToken = "",
   setAccessToken,
    data,
    setData,
    backToChoice
}) => {
    const [selectedFiles, setSelectedFiles] = useState<any>([]);
    const [requiredFiles, setRequiredFiles] = useState<number>(1);
    const [isFrench] = useState<boolean>(true);
    const { user } = useContext(UserContext);
    const [isShareID, setIsShareID] = useState<boolean>(isByVideo);
    const [showSponsorModal, setShowSponsorModal] = useState<boolean>(false);

    const userType = user?.type;
    const credits = user?.credits ?? 0;

    // Quota gratuit mensuel (3/mois/outil). Cet écran sert deux outils : l'ajout
    // de document et la vérification (photo, vidéo, Dark Web). On ne bascule sur
    // « acheter » que si AUCUN des deux n'est disponible, ni gratuitement ni au solde.
    const [detectionQuota, setDetectionQuota] = useState<any>(null);
    const [quotaLoaded, setQuotaLoaded] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        loadDetectionQuota(userType).then((q: any) => {
            if (cancelled) return;
            setDetectionQuota(q);
            setQuotaLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [userType]);

    // Tant que le quota est inconnu, on laisse l'écran ouvert (évite un écran de
    // blocage qui clignote). Si l'appel a échoué, on retombe sur l'ancien critère.
    const canUseAnyTool = !quotaLoaded
        ? true
        : detectionQuota
            ? detectionQuota.credits > 0 ||
              detectionQuota.freeRemaining(DetectionTool.documentAdd) > 0 ||
              detectionQuota.freeRemaining(DetectionTool.documentVerify) > 0
            : credits > 0;

    const getToken = async () => { // SHARE ID TOKEN
        const token = await getShareIdTokenAndApplicantId(userType ?? "");
        if (token) {
            setAccessToken(token.token);
            setApplicant_id(token.applicant_id);
            return { tokenShareId: token.token, applicantId: token.applicant_id};
        }
        return { tokenShareId: null, applicantId: null};

    };

    useEffect(() => {
        const sdkFlex: any = document.querySelector(".videoVerification .sdk-flex");
        if (!sdkFlex) return;
        sdkFlex.style.display = "none";
    }, [accessToken]);

    const handleChange = (value: string) => {
        onChangeType(value);
        setSelectedFiles([]);
        if (value === "Passeport") {
            setRequiredFiles(1);
        } else {
            setRequiredFiles(1);
        }
    };

    const generateURLWithShareIdPhoto = async (contenuReponseShareIDPhoto: any, applicantId: any) => {
        try {
            const responseFile = await generateReportWithShareIdPhoto(userType, selectedFiles, contenuReponseShareIDPhoto!, applicantId);
            setVerifyReportURL && setVerifyReportURL(responseFile.pdfPath);
        } catch (error: any) {
            // console.error("Une erreur s'est produite :", error);
            setErrorMessage && setErrorMessage(error.message);
        }
    };

    const generateURLWithShareIdVideo = async (contenuReponseShareIDVideo: any, applicantId: any) => {
        try {
            const responseFile = await generateReportWithShareIDVideo(userType, contenuReponseShareIDVideo!, applicantId);
            setVerifyReportURL && setVerifyReportURL(responseFile.pdfPath);
        } catch (error: any) {
            // console.error("Une erreur s'est produite :", error);
            setErrorMessage && setErrorMessage(error.message);
        }
    };

    const handleVerification = async () => {
        // Garde d'entrée : 3 vérifications gratuites par mois pour cet outil, puis solde.
        if (!(await canStartDetection(userType, DetectionTool.documentAdd))) {
            setErrorType && setErrorType({ status: 402, message: NO_VERIFICATION_LEFT });
            return;
        }
        let { tokenShareId, applicantId } = await getToken();

        if (tokenShareId) {
            setIsScanning(true);
            setIsScanningLoadingText("Préparation de la demande");
            let response; // le retour du body de shareID
           
            try {
                //console.log(`Type du document uploadé est : ${typeof selectedFiles}`);
                response = await getDocumentHighestAnalysisEver(userType, selectedFiles, tokenShareId);
                if (user?.type === "partner") {
                    if (response) {
                        generateURLWithShareIdPhoto(response, applicantId);
                    }
                }

                if (response.ocr != null) {
                    await consumeDetection(userType, DetectionTool.documentAdd); //3 gratuites/mois, puis 1 vérification débitée
                    setIsScanned && setIsScanned(true);
                    setDocument && setDocument(formatShareIDPhotoResultToDocument(response));
                    setData && setData(response);
                    //console.log(`response: ${JSON.stringify(response)}`)
                    const test = await formatShareIDPhotoResultToDocument(response);
                    console.log(`response before being setted: ${JSON.stringify(test)}`)
                    //console.log(`response formatted: ${JSON.stringify(document)}`)
                }
                else {
                    const errorType = {
                        status: 3000,
                        message: "Le document est flou, obstrué ou mal éclairé",
                    };

                    setErrorType && setErrorType(errorType);
                }


            } catch (error: any) {
                handleErrorVerification(error);
                if (user?.type === "partner") {
                    if (response) {
                        generateURLWithShareIdPhoto(response, applicantId);
                    }
                }
            } finally {
                setIsScanning(false);
            }
        }
    };

    const handleDarkWebVerification = async () => {
        // Recherche Dark Web : partage le quota de la vérification de document (décision Q).
        if (!(await canStartDetection(userType, DetectionTool.documentVerify))) {
            setErrorType && setErrorType({ status: 402, message: NO_VERIFICATION_LEFT });
            return;
        }
        let { tokenShareId, applicantId } = await getToken();
        if (tokenShareId) {
            setIsScanning(true);
            setIsScanningLoadingText("Recherche sur le dark Web...");
            let response;
            try {
                
                response = await getDocumentHighestAnalysisEver(userType, selectedFiles, tokenShareId);

                if (user?.type === "partner") {
                    if (response) {
                        setIsScanningLoadingText("Géneration du rapport de l'analyse");
                        generateURLWithShareIdPhoto(response, applicantId);
                    }
                }
                if (response.ocr != null) {
                    // Recherche Dark Web : partage le quota de la vérification de document (décision Q).
                    await consumeDetection(userType, DetectionTool.documentVerify);
                    setIsScanned && setIsScanned(true);
                    setDocument && setDocument(formatShareIDPhotoResultToDocument(response));
                    setData && setData(response);
                }
                else {
                    const errorType = {
                        status: 3000,
                        message: "Le document est flou, obstrué ou mal éclairé",
                    };

                    setErrorType && setErrorType(errorType);
                }
            } catch (error: any) {
                handleErrorVerification(error);
            } finally {
                setIsScanning(false);
            }
        }
    };

    const handleErrorVerification = (error: any) => {
        const errorStatus = error.response?.status;
        const responseError = error.response?.data?.error;
        const error400Message =
            responseError === "Document manquantes, Veuillez présenter un autre document d'identité."
                ? "Document non reconnu."
                : "Document non conforme.";

        const errors: ErrorType[] = [
            {
                status: 500,
                message: "Erreur de serveur interne",
            },
            {
                status: 504,
                message: "L'analyse de votre document prend plus de temps que prévu. Veuillez réessayer ultérieurement.",
            },
            {
                status: 503,
                message: responseError,
            },
            {
                status: 400,
                message: error400Message,
            },
            {
                status: 401,
                message: "Une erreur est survenue. Veuillez réessayer.",
            },
        ];
        // Si l'error.status n'est pas dans la liste des erreurs, on affiche un message par défaut
        const errorType = errors.find((error) => error.status === errorStatus) || {
            status: 3000,
            message: "Une erreur est survenue. Veuillez réessayer.",
        };

        //  const errorType = errors.filter((error: any) => error.status === errorStatus)[0];

        setErrorType && setErrorType(errorType);
    };

    return (
        <div className="addDocumentForm card">
            {!canUseAnyTool ? (
                    <>
                        <Alert type="error">{NO_VERIFICATION_LEFT}</Alert>
                        <BuyCreditsCard showSponsorModal={showSponsorModal} setShowSponsorModal={setShowSponsorModal}/>
                        {
                        showSponsorModal && (
                            <SponsorShipModal isOpen={showSponsorModal}
                            onClose={() => setShowSponsorModal(false)}
                            />
                        )
                    }
                    </>
                ) : (
                        <>
                            {isShareID && !isDarkWeb ? (
                                <ShareIDVerification
                                    applicantId={applicant_id}
                                    accessToken={accessToken}
                                    setIsShareID={setIsShareID}
                                    setIsScanning={setIsScanning}
                                    setIsScanningLoadingText={setIsScanningLoadingText}
                                    setIsScanned={setIsScanned}
                                    setErrorType={setErrorType}
                                    setDocument={setDocument}
                                    errorType={errorType}
                                    generateURLWithShareIdVideo={generateURLWithShareIdVideo}
                                />
                            ) : (
                                <>
                                    <div>
                                        {errorType?.status !== 0 && <Alert type="error">{errorType?.message}</Alert>}
                                        <DocumentTypeSelect
                                            handleChange={handleChange}
                                            isVerification={isVerification}
                                            type={type}
                                        />
                                        <div className="addDocumentForm__buttons">
                                            <FileDownloadInstructions />
                                            {type && (
                                                <DocumentUpload
                                                    type={type}
                                                    setSelectedFiles={setSelectedFiles}
                                                    selectedFiles={selectedFiles}
                                                />
                                            )}
                                        </div>
                                        {!isFrench && !allowForeignDocuments && (
                                            <Alert type="error">
                                                Attention, les documents non français ne sont pas encore pris en charge. Veuillez nous contacter pour plus d'informations.
                                            </Alert>
                                        )}
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        {backToChoice && (
                                            <Button
                                                buttonType="button"
                                                label="Retour"
                                                style={Button_Style.OUTLINED}
                                                onClick={() => backToChoice()}
                                            />
                                        )}
                                        <div style={{ marginLeft: "auto" }}>
                                            <Button
                                                type={userType === "partner" ? Button_Type.SECONDARY : Button_Type.PRIMARY}
                                                label={isVerification ? "Vérifier" : "Ajouter"}
                                                style={Button_Style.OUTLINED}
                                                disabled={selectedFiles.length < requiredFiles || (!isFrench && !allowForeignDocuments)}
                                                onClick={() => (isDarkWeb ? handleDarkWebVerification() : handleVerification())}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                        </>
                )
            }
            
        </div>
    );
};

export default AddDocumentForm;
